import React, { useState, useEffect, useRef } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { merge } from 'highcharts';
import { unitFormat } from 'components/features/new-report/utils/constructor';
import { renderToStaticMarkup } from 'react-dom/server';
import { ExtraTimeSeriesData } from 'components/features/home/assetStuffing/AssetStuffing';
import { IconOnChart } from 'components/features/home/assetStuffing/AssetStuffingChart';
import { arrangeDateForChart } from 'components/features/utils/util';

type Data = {
  dataRange: {
    from: Date;
    to: Date;
  };
  targetAmount: number;
  achievements: {
    date: Date;
    achievement: number | null;
  }[];
  benchmarks: { date: Date; benchmark: number }[];
  extraData: ExtraTimeSeriesData[];
};

const isIn = (date: Date) => (min: Date) => (max: Date): boolean =>
  date >= min && date <= max;

type Props = {
  data: Data;
  dateFormatter: (date: Date) => string;
  targetDate: Date;
};

const getToday = () => new Date();

export const calcPlotDate = (
  data: { date: Date; value: number | null }[]
): Date | null => {
  return (date => {
    if (!date) return date;
    if (getToday().getTime() - date.getTime() > 7 * 24 * 60 * 60 * 1000)
      return null;
    return date;
  })(
    data
      .flatMap(datum => (datum.value ? [datum] : []))
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .reduce((_, v) => v.date, null as Date | null)
  );
};

const arrangeChartData = (data: Data) => (
  isPlotDate: (date: Date) => boolean
): Highcharts.SeriesOptionsType[] => {
  return [
    {
      turboThreshold: 15000,
      name: '実績',
      type: 'line',
      data: data.achievements.map(
        (achievement, i, arr): Highcharts.PointOptionsObject => {
          return {
            x: achievement.date.getTime(),
            y: achievement.achievement,
            marker: isPlotDate(achievement.date)
              ? { enabled: true, ...achievementMarker }
              : { enabled: false },
            dataLabels:
              i === 0
                ? [
                    {
                      formatter(): string {
                        return renderToStaticMarkup(
                          <TargetAchieveLabel color={achievementColor} />
                        );
                      },
                      allowOverlap: true,
                      overflow: 'allow',
                      crop: false,
                      enabled: true,
                      useHTML: true,
                    },
                  ]
                : [],
          };
        }
      ),
      color: achievementColor,
      marker: {
        enabled: false,
        fillColor: achievementColor,
        symbol: 'circle',
        radius: 4,
        lineWidth: 2,
      },
      lineWidth: 3,
    },

    {
      turboThreshold: 15000,
      name: '計画',
      type: 'line',
      data: data.benchmarks.map(
        (benchmark): Highcharts.PointOptionsObject => {
          return {
            x: benchmark.date.getTime(),
            y: benchmark.benchmark,
          };
        }
      ),
      color: benchmarkColor,
      marker: {
        enabled: false,
      },
      lineWidth: 2,
    },
    {
      turboThreshold: 15000,
      name: '目標値',
      type: 'line',
      dashStyle: 'Dot',
      color: benchmarkColor,
      data: [
        {
          x: data.achievements[0].date.getTime(),
          y: data.targetAmount,
        },
        {
          x: data.achievements[data.achievements.length - 1].date.getTime(),
          y: data.targetAmount,
          dataLabels: [
            {
              formatter(): string {
                return renderToStaticMarkup(
                  <TargetAchieveLabel color={benchmarkColor} />
                );
              },
              allowOverlap: true,
              overflow: 'allow',
              crop: false,
              enabled: true,
              useHTML: true,
            },
          ],
        },
      ],
      marker: {
        enabled: false,
      },
    },
  ];
};

const achievementColor = '#eab308';
const benchmarkColor = '#60a5fa';

const achievementMarker: Highcharts.PointMarkerOptionsObject = {
  radius: 7,
  lineWidth: 4,
  symbol: 'circle',
  lineColor: achievementColor,
  fillColor: 'white',
};

const TooltipItem = ({
  name,
  color,
  value,
}: {
  name: string;
  color: string;
  value: number;
}) => {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'min-content min-content min-content',
          placeItems: 'center start',
          gap: '2px',
        }}>
        <div
          style={{
            borderRadius: '50%',
            height: '5px',
            width: '5px',
            gridColumn: 1,
            backgroundColor: color,
          }}
        />
        <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
        <span
          style={{
            fontWeight: 'bold',
            fontSize: '10px',
            gridColumn: 3,
          }}>
          {unitFormat(value)}円
        </span>
      </div>
    </>
  );
};

const Tooltip = ({
  date,
  children,
}: {
  date: Date;
  children: React.ReactNodeArray;
}) => {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gap: '2px',
          gridAutoRows: 'min-content',
          gridAutoFlow: 'row',
        }}>
        <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
          {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
        </div>
        {children}
      </div>
    </>
  );
};

export const TransitionChart = (props: Props): React.ReactElement => {
  const dateLabelWhiskerHeightPx = 25;
  const plotDate = calcPlotDate(
    props.data.achievements.map(datum => ({
      date: datum.date,
      value: datum.achievement,
    }))
  );

  const [icons, setIcons] = useState<IconOnChart[]>([]);
  const [toolTipPosition, setToolTipPosition] = useState<{
    x: number;
    y: number;
    width: number;
    date: Date | null;
  }>({ x: 0, y: 0, width: 0, date: null });
  const [showToolTip, setShowToolTip] = useState<boolean>(false);

  const [options, setOptions] = useState<Highcharts.Options>({
    credits: {
      enabled: false,
    },
    time: {
      useUTC: false,
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: '',
    },
    yAxis: {
      opposite: true,
      labels: {
        formatter(): string {
          if (typeof this.value === 'string') return this.value;
          return `${unitFormat(this.value, 1)}`;
        },
      },
      title: {
        text: '',
      },
    },
    xAxis: {
      type: 'datetime',
      labels: {
        formatter(): string {
          if (typeof this.value === 'string') return this.value;
          return props.dateFormatter(new Date(this.value));
        },
      },
      plotLines:
        plotDate &&
        isIn(plotDate)(props.data.dataRange.from)(props.data.dataRange.to)
          ? [
              {
                color: achievementColor,
                width: 1,
                value: ((date: Date): Date =>
                  new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate()
                  ))(plotDate).getTime(),
                label: {
                  align: 'center',
                  useHTML: true,
                  text: renderToStaticMarkup(
                    <DateLabel
                      title={(date =>
                        `${date.getMonth() + 1}/${date.getDate()}`)(plotDate)}
                      dateLabelWhiskerHeightPx={dateLabelWhiskerHeightPx}
                    />
                  ),
                  rotation: 0,
                  verticalAlign: 'bottom',
                  x: 0,
                  y: 10,
                },
              },
            ]
          : [],
    },
    legend: {
      verticalAlign: 'top',
      align: 'left',
      itemStyle: { fontWeight: 'normal' },
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      shared: true,
      formatter: function() {
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ?? []).map((point, i) => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
                key={i}
              />
            ))}
          </Tooltip>
        );
      },
    },
  });

  useEffect(() => {
    setOptions(prev => {
      return merge({
        ...prev,
        chart: {
          height: 250,
          spacingLeft: 22.5,
          spacingRight: 22.5,
          marginBottom: 55,
          events: {
            redraw: function() {
              if (ref.current) {
                const chart = ref.current.chart;
                drawIcons({ chart, icons, extraData: props.data.extraData });
              }
            },
          },
        },
        series: arrangeChartData(props.data)(
          date =>
            plotDate !== null &&
            date.getFullYear() === plotDate.getFullYear() &&
            date.getMonth() === plotDate.getMonth() &&
            date.getDate() === plotDate.getDate()
        ),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const drawIcons = ({
    chart,
    extraData,
  }: {
    chart: Highcharts.Chart;
    icons: IconOnChart[];
    extraData: ExtraTimeSeriesData[];
  }): void => {
    const iconSize = 15;
    const margin = 5;
    icons.forEach(({ icon }) => {
      if (icon.renderer) icon.destroy();
    });
    const xAxis = chart.xAxis[0];

    const incorrect = extraData.find(it => it.type === 'IncorrectSign');
    if (!incorrect) return;
    const signSeries = incorrect.value
      .filter(it => it.date && it.value)
      .map(it => it.date);

    signSeries.forEach(date => {
      const x = xAxis.toPixels(arrangeDateForChart(date).getTime(), true);
      if (!chart.isInsidePlot(x, chart.plotTop)) return;
      const icon = chart.renderer
        .image(
          '/static/images/icon/icon-info.svg',
          x,
          chart.plotTop + chart.plotHeight - 7,
          iconSize,
          iconSize
        )
        .add();
      icon.toFront();
      const tooltipWidth = 150;
      let tx = x - tooltipWidth - margin;
      if (!chart.isInsidePlot(tx, chart.plotTop)) {
        tx = x + iconSize + margin;
      }

      icon.element.addEventListener('mouseover', () => {
        setToolTipPosition({
          x: tx,
          y: chart.chartHeight - chart.plotTop - chart.plotHeight,
          width: tooltipWidth,
          date: date,
        });
        setShowToolTip(true);
      });
      icon.element.addEventListener('mouseout', () => {
        setShowToolTip(false);
      });

      icons.push({ icon });
      setIcons(icons);
    });
  };

  const Tip = ({
    pos,
    show,
  }: {
    pos: { x: number; y: number; width: number; date: Date | null };
    show: boolean;
  }) => {
    return (
      <div
        className="p-2 absolute bg-white rounded-md shadow-md text-xs w-1/2 border border-gray-300"
        style={{
          display: show ? 'block' : 'none',
          zIndex: 999,
          left: pos.x,
          bottom: pos.y,
          maxWidth: pos.width,
        }}>
        <div className="flex flex-row">
          <div
            className="icon-info text-sm"
            id="icon-info"
            style={{ color: '#94A3B8' }}></div>
          <div className="ml-1 text-1.5xs">
            <div>
              集計が正常に完了しなかった可能性があるためこのデータは参考値です。
            </div>
            <div className="mt-1">
              {pos.date !== null
                ? `${pos.date.getFullYear()}/${pos.date.getMonth() +
                    1}/${pos.date.getDate()}`
                : '----/--/--'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <div className="relative">
      <HighchartsReact
        {...{
          highcharts: Highcharts,
          options,
          ref,
        }}
      />
      <Tip pos={toolTipPosition} show={showToolTip} />
    </div>
  );
};

const DateLabel = (props: {
  title: string;
  dateLabelWhiskerHeightPx: number;
}): React.ReactElement => {
  return (
    <div
      className="grid relative"
      style={{
        placeItems: 'center',
        gridTemplateRows: 'auto auto',
      }}>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div
          className="border-r"
          style={{
            borderColor: achievementColor,
            height: `${props.dateLabelWhiskerHeightPx}px`,
          }}></div>
        <div className=""></div>
      </div>
      <div
        className="rounded p-1 text-white text-xs"
        style={{ backgroundColor: achievementColor }}>
        {props.title}
      </div>
    </div>
  );
};

const TargetAchieveLabel = ({
  color,
}: {
  color: typeof benchmarkColor | typeof achievementColor;
}): React.ReactElement => {
  return (
    <>
      <div
        className="rounded-full p-1 text-white relative"
        style={{ backgroundColor: color }}>
        <div
          className="absolute w-0 h-0 bottom-0 right-1/2"
          style={{
            transform: 'translate(50%,90%)',
            borderStyle: 'solid',
            borderWidth: '8px 2px 0 2px',
            borderColor: `${color} transparent transparent transparent`,
          }}></div>
        <div className="icon-flag"></div>
      </div>
    </>
  );
};
